import type { i18n as I18n } from "i18next";
import { AppLanguage } from "./config";

/** Branded strings for one language. Anything omitted keeps the value shipped in the locale files. */
export interface BrandingStrings {
  /** Product name: header title, browser tab, conversation-less screen titles. */
  appName?: string;
  /** Tagline shown under the header title. */
  subtitle?: string;
  /**
   * Line under the product name in the About modal, used verbatim. Independent of `subtitle`, and
   * of `appName` too: the shipped line spells out the shipped name, so a rename sets both.
   */
  description?: string;
}

/**
 * Deployment-provided product identity, keyed by language like `QuickPrompts` — the two live side
 * by side in the same configuration file. A deployment that provides nothing renders exactly as
 * before, and a language left out keeps the strings shipped in the locale files.
 */
export interface Branding {
  [AppLanguage.en]?: BrandingStrings;
  [AppLanguage.fr]?: BrandingStrings;
}

const ABOUT_NS = "about";
const COMMON_NS = "common";

/**
 * Trimmed value, or undefined when the deployment left the entry empty — or put something other
 * than a string there. The values come from a hand-editable JSON file, so the declared type is a
 * hope, not a guarantee, and a throw here would stop the app before its first render.
 */
const clean = (value: unknown): string | undefined => {
  const trimmed = typeof value === "string" ? value.trim() : undefined;
  return trimmed ? trimmed : undefined;
};

/**
 * Overwrites, in every language the deployment named, exactly the strings it provided — nothing is
 * derived here. Runs after `i18next.init`, against the in-memory resource store; the locale files
 * stay the defaults for everything not overridden.
 */
export const applyBranding = (i18n: I18n, branding: Branding): void => {
  for (const language of Object.values(AppLanguage)) {
    const strings = branding[language];
    const appName = clean(strings?.appName);
    const subtitle = clean(strings?.subtitle);
    const description = clean(strings?.description);

    const about: Record<string, string> = {
      ...(appName && { headertitle: appName }),
      ...(subtitle && { headerSubtitle: subtitle }),
      ...(description && { description }),
    };
    if (Object.keys(about).length) i18n.addResourceBundle(language, ABOUT_NS, about, true, true);
    if (appName) i18n.addResourceBundle(language, COMMON_NS, { appName }, true, true);
  }
};
